/**
 * Curated per-clip video filters — overlays, theme tones, and special effects
 * that work in editor preview and server export.
 */
import { cssPreviewForNonOverlayFilters } from '@/lib/videoFilterOverlays';

export const FILTER_CATEGORIES = [
  { id: 'overlays', label: 'Overlays' },
  { id: 'theme', label: 'Theme & Tone' },
  { id: 'special', label: 'Special Effects' },
];

/** @type {Array<{ id: string, label: string, category: string, useCase?: string, defaultIntensity: number, overlay?: boolean, ffmpeg?: string | null }>} */
export const VIDEO_FILTERS = [
  // Overlays — gradient layers on top of footage
  { id: 'glossy', label: 'Glossy', category: 'overlays', useCase: 'Polished glass shine', defaultIntensity: 55, overlay: true },
  { id: 'sunlight', label: 'Sunlight', category: 'overlays', useCase: 'Warm sun rays from the corner', defaultIntensity: 50, overlay: true },
  { id: 'vignette', label: 'Vignette', category: 'overlays', useCase: 'Dark cinematic edges', defaultIntensity: 50, overlay: true, ffmpeg: 'vignette' },
  { id: 'sepia', label: 'Sepia', category: 'overlays', useCase: 'Vintage brown tone', defaultIntensity: 70, overlay: true, ffmpeg: 'colorchannelmixer' },
  { id: 'light_leak', label: 'Light Leak', category: 'overlays', useCase: 'Warm light bleeding at edges', defaultIntensity: 45, overlay: true },

  // Theme & tone — color grade
  { id: 'black_white', label: 'Black & White', category: 'theme', useCase: 'Classic monochrome', defaultIntensity: 100, ffmpeg: 'hue=s=0' },
  { id: 'warmth', label: 'Golden Warmth', category: 'theme', useCase: 'Golden hour look', defaultIntensity: 50, ffmpeg: 'colorchannelmixer' },
  { id: 'cool_tone', label: 'Cool Cinematic', category: 'theme', useCase: 'Blue-teal grade', defaultIntensity: 50 },
  { id: 'vibrance', label: 'Vibrance', category: 'theme', useCase: 'Rich, vivid colors', defaultIntensity: 55, ffmpeg: 'eq=saturation={v}' },
  { id: 'contrast', label: 'Contrast', category: 'theme', useCase: 'Punchy cinematic pop', defaultIntensity: 55, ffmpeg: 'eq=contrast={v}' },

  // Special effects
  { id: 'glow', label: 'Glow', category: 'special', useCase: 'Dreamy highlight bloom', defaultIntensity: 45, ffmpeg: 'gblur=sigma=2,eq=brightness=0.05' },
  { id: 'film_grain', label: 'Film Grain', category: 'special', useCase: 'Organic film texture', defaultIntensity: 35, ffmpeg: 'noise=alls={v}:allf=t' },
  { id: 'lens_flare', label: 'Lens Flare', category: 'special', useCase: 'Light streaks and halos', defaultIntensity: 40 },
  { id: 'fade_in', label: 'Fade In', category: 'special', useCase: 'Fade up at clip start', defaultIntensity: 50, ffmpeg: 'fade=t=in' },
  { id: 'fade_out', label: 'Fade Out', category: 'special', useCase: 'Fade down at clip end', defaultIntensity: 50, ffmpeg: 'fade=t=out' },
];

export const FILTER_IDS = new Set(VIDEO_FILTERS.map((f) => f.id));

export function getFilterById(id) {
  return VIDEO_FILTERS.find((f) => f.id === id) || null;
}

export function normalizeFilterId(id) {
  const key = String(id || '').trim().toLowerCase().replace(/-/g, '_');
  if (!key || key === 'none') return null;
  return FILTER_IDS.has(key) ? key : null;
}

export function defaultIntensityForFilter(id) {
  return getFilterById(normalizeFilterId(id))?.defaultIntensity ?? 50;
}

export function clampFilterIntensity(value, filterId) {
  const def = getFilterById(normalizeFilterId(filterId));
  const min = def?.minIntensity ?? 0;
  const max = def?.maxIntensity ?? 100;
  const n = Number(value);
  if (!Number.isFinite(n)) return def?.defaultIntensity ?? 50;
  return Math.max(min, Math.min(max, Math.round(n)));
}

export function filtersByCategory() {
  const map = Object.fromEntries(FILTER_CATEGORIES.map((c) => [c.id, []]));
  for (const f of VIDEO_FILTERS) {
    if (map[f.category]) map[f.category].push(f);
  }
  return map;
}

export function filterShortLabel(id) {
  const f = getFilterById(normalizeFilterId(id));
  if (!f) return '';
  return f.label.split(' ')[0];
}

/** CSS filter string for editor preview (overlays use VideoFilterOverlayStack). */
export function cssPreviewForFilters(filters) {
  return cssPreviewForNonOverlayFilters(normalizeClipFilters(filters));
}

/** Inline style for video/image preview (theme + special filters). */
export function clipFilterPreviewStyle(rawFilters) {
  const css = cssPreviewForFilters(rawFilters);
  if (!css) return undefined;
  return { filter: css, WebkitFilter: css };
}

/** Read filters from a timeline clip (camelCase or snake_case). */
export function clipFiltersFromClip(clip) {
  return normalizeClipFilters(clip?.videoFilters ?? clip?.video_filters);
}

export function normalizeClipFilters(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((f) => {
      const id = normalizeFilterId(f?.id ?? f?.filter_id);
      if (!id) return null;
      return { id, intensity: clampFilterIntensity(f?.intensity, id) };
    })
    .filter(Boolean);
}

/** Build FFmpeg -vf chain for local wasm export. */
export function buildFfmpegFilterChain(filters) {
  const specs = normalizeClipFilters(filters);
  if (!specs.length) return null;
  const parts = [];
  for (const { id, intensity } of specs) {
    const t = intensity / 100;
    switch (id) {
      case 'black_white':
        parts.push('hue=s=0');
        break;
      case 'warmth':
      case 'sepia':
        parts.push('colorchannelmixer=.393:.769:.189:0:.349:.686:.168:0:.272:.534:.131');
        break;
      case 'vibrance':
        parts.push(`eq=saturation=${(1 + t * 1.0).toFixed(3)}`);
        break;
      case 'contrast':
        parts.push(`eq=contrast=${(1 + t * 0.8).toFixed(3)}`);
        break;
      case 'glossy':
        parts.push(`eq=contrast=${(1 + t * 0.15).toFixed(3)}`, `eq=saturation=${(1 + t * 0.1).toFixed(3)}`);
        break;
      case 'sunlight':
        parts.push(`eq=brightness=${(t * 0.1).toFixed(3)}`, `eq=saturation=${(1 + t * 0.2).toFixed(3)}`);
        break;
      case 'vignette':
        parts.push('vignette=angle=PI/4:mode=forward');
        break;
      case 'glow':
        parts.push(`gblur=sigma=2,eq=brightness=${(t * 0.08).toFixed(3)}`);
        break;
      case 'film_grain':
        parts.push(`noise=alls=${Math.round(10 + t * 40)}:allf=t`);
        break;
      case 'fade_in':
        parts.push('fade=t=in:st=0:d=0.6');
        break;
      case 'fade_out':
        parts.push('fade=t=out:st=0:d=0.6');
        break;
      case 'cool_tone':
        parts.push(`eq=brightness=${(1 - t * 0.04).toFixed(3)}`, `eq=saturation=${(1 + t * 0.3).toFixed(3)}`);
        break;
      case 'lens_flare':
        parts.push(`eq=brightness=${(1 + t * 0.15).toFixed(3)}`, `eq=saturation=${(1 + t * 0.12).toFixed(3)}`);
        break;
      case 'light_leak':
        parts.push(`eq=brightness=${(1 + t * 0.1).toFixed(3)}`, `eq=saturation=${(1 + t * 0.15).toFixed(3)}`);
        break;
      default:
        break;
    }
  }
  return parts.length ? parts.join(',') : null;
}
